import { Flex, Icon, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiCheck } from "react-icons/fi";
import type { SourcingEventV2Dto } from "../../../../autogen/bff-api";
import type { TimelinePhaseKey } from "./deriveTimelinePhase";
import type { NegotiationStage } from "./negotiationStages";
import { phaseDate } from "./phaseDates";
import { phaseLabel } from "./phaseLabels";
import { useTimelineDate } from "./useTimelineDate";

const dotShadow = (isActive: boolean, isDone: boolean): string =>
  isActive
    ? "0 0 0 4px var(--chakra-colors-smBackground), 0 0 0 6px var(--chakra-colors-smPrimary)"
    : isDone
      ? "0 0 0 1.5px var(--chakra-colors-smPrimary)"
      : "0 0 0 1.5px var(--chakra-colors-smBorder)";

interface Props {
  event: SourcingEventV2Dto;
  phaseKey: TimelinePhaseKey;
  stepNumber: number;
  isActive: boolean;
  isDone: boolean;
  latestStage: NegotiationStage | null;
}

export const PhaseStep = ({
  event,
  phaseKey,
  stepNumber,
  isActive,
  isDone,
  latestStage,
}: Props) => {
  const { t } = useTranslation();
  const timelineDate = useTimelineDate();
  const filled = isActive || isDone;
  const date = phaseDate(event, phaseKey, latestStage);

  return (
    <Flex
      flex={{ md: "1 1 0" }}
      direction={{ base: "row", md: "column" }}
      align={{ base: "flex-start", md: "center" }}
      gap={{ base: 3, md: 2 }}
      px={{ md: 2 }}
      minW="0"
      aria-current={isActive ? "step" : undefined}
    >
      <Flex
        align="center"
        justify="center"
        boxSize="7"
        flexShrink={0}
        borderRadius="full"
        fontSize="xs"
        fontWeight="bold"
        bg={filled ? "smPrimary" : "smBackground"}
        color={filled ? "white" : "smMuted"}
        boxShadow={dotShadow(isActive, isDone)}
      >
        {isDone ? <Icon as={FiCheck} boxSize="3.5" /> : stepNumber}
      </Flex>
      <Flex direction="column" align={{ base: "flex-start", md: "center" }} gap="0.5" minW="0">
        <Text
          fontSize="sm"
          fontWeight={isActive ? "semibold" : "medium"}
          color={isActive ? "smText" : "smMuted"}
          textAlign={{ base: "left", md: "center" }}
          lineHeight="1.2"
        >
          {phaseLabel(phaseKey, event, t)}
        </Text>
        {date && (
          <Text
            fontSize="xs"
            color={isActive ? "smText" : "smMuted"}
            textAlign={{ base: "left", md: "center" }}
            lineHeight="1.3"
          >
            {timelineDate(date)}
          </Text>
        )}
        {date?.planned && (
          <Text fontSize="xs" color="smMuted" lineHeight="1.2" whiteSpace="nowrap">
            {t("Planned date")}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
